<script lang="ts" setup>
// Shared mobile shell for the employee home, Clocks (time clock and timesheet), CompanyCam (job photos) and
// Sales. Every text goes through ft() so the app works in English and Spanish.
useAppearanceClass()
const {
  departmentChosen,
  isSignedIn,
  refreshSession,
  session,
  setDepartment,
  signOut,
} = useField()
const toast = useToast()
const { failed, pending, start, uploading } = useFieldQueue()
const online = useOnline()
const router = useRouter()
// The router's current route, not useRoute(): in a layout useRoute() only changes once the next page has
// rendered, and the department and location screens below replace the page, so the menu would get stuck.
const route = computed(() => router.currentRoute.value)

// Installed on the home screen there's no browser back button, so the app has its own.
const canGoBack = computed(
  () => route.value.path.replace(/\/$/, '') !== '/field'
)
const goBack = () => {
  // Opened straight on a job (from the icon or a link): there is no earlier screen, go home.
  if (window.history.state?.back) router.back()
  else navigateTo('/field', { replace: true })
}
const location = useFieldLocation()

const isPhotoPage = computed(() =>
  /^\/field\/(photos|assign|project|job)(\/|$)/.test(route.value.path)
)
const needsPhotoLocation = computed(
  () =>
    isSignedIn.value &&
    isPhotoPage.value &&
    Boolean(session.value?.allowed_departments.length)
)
let stopLocation: (() => void) | undefined
let stop: (() => void) | undefined
onMounted(() => {
  document.documentElement.lang = fieldLocale.value
  // Keep a copy of the app on the phone so it opens without signal (production builds only).
  if (!import.meta.dev && 'serviceWorker' in navigator) {
    navigator.serviceWorker
      .register('/field-sw.js', { scope: '/field' })
      .catch(() => undefined)
  }
  // Ask the browser not to clear photos saved on the phone when storage runs low.
  navigator.storage?.persist?.().catch(() => undefined)
  // Load every screen of the app now, so they open later even without signal.
  for (const path of [
    '/field/timesheet',
    '/field/marketing',
    '/field/sales/lead/0',
    '/field/sales/estimate/0',
    '/field/sales/contract/0',
    '/field/photos',
    '/field/assign',
    '/field/project/0',
    '/field/job/0',
  ]) {
    preloadRouteComponents(path).catch(() => undefined)
  }

  stop = start()
  stopLocation = watch(
    needsPhotoLocation,
    (needed) => (needed ? location.start() : location.stop()),
    { immediate: true }
  )
  if (isSignedIn.value) refreshSession()
})
onBeforeUnmount(() => {
  stopLocation?.()
  stop?.()
  location.stop()
})

// Photo screens use location and a department. The worker home and time clock open independently.
watchEffect(() => {
  if (
    import.meta.client &&
    canGoBack.value &&
    (!isSignedIn.value ||
      (isPhotoPage.value && !session.value?.allowed_departments.length))
  ) {
    navigateTo('/field', { replace: true })
  }
})
const locked = computed(
  () => needsPhotoLocation.value && location.status.value !== 'granted'
)

// Departments: choose one after signing in (when there are several); switch any time from the top bar.
const allowedDepartments = computed(
  () => session.value?.allowed_departments ?? []
)
const needsDepartment = computed(
  () =>
    isSignedIn.value &&
    isPhotoPage.value &&
    !departmentChosen.value &&
    allowedDepartments.value.length > 1
)
const showDepartments = ref(false)
const switching = ref(false)
const chooseDepartment = async (department: FieldDepartment) => {
  switching.value = true
  try {
    await setDepartment(department)
    showDepartments.value = false
  } catch (error) {
    toast.add({
      detail: fieldErrorMessage(error),
      life: 5000,
      severity: 'error',
      summary: ft('Could not switch department'),
    })
  } finally {
    switching.value = false
  }
}
const isIos = computed(
  () =>
    import.meta.client && /iPhone|iPad|iPod/i.test(navigator.userAgent ?? '')
)
const retryLocation = () => {
  // After changing the setting, some browsers only ask again on a fresh page.
  if (location.status.value === 'denied') window.location.reload()
  else location.start()
}

// Language: English or Spanish, saved on this phone.
const otherLocale = computed(() => (fieldLocale.value === 'es' ? 'en' : 'es'))
const switchLanguage = () => setFieldLocale(otherLocale.value)

const onSignOut = async () => {
  if (
    pending.value.length &&
    !window.confirm(
      ft(
        '{count} photo(s) have not uploaded yet. They stay on this phone and upload after you sign in again. Sign out?',
        { count: pending.value.length }
      )
    )
  )
    return
  await signOut()
  navigateTo('/field')
}

useHead({
  titleTemplate: (title) => (title ? `${title} | SOS Field` : 'SOS Field'),
  link: [
    { rel: 'manifest', href: '/field.webmanifest' },
    { rel: 'apple-touch-icon', href: '/images/field-icon-180.png' },
    { rel: 'icon', type: 'image/svg+xml', href: '/images/field-icon.svg' },
  ],
  meta: [
    { name: 'theme-color', content: '#50427d' },
    { name: 'apple-mobile-web-app-capable', content: 'yes' },
    { name: 'mobile-web-app-capable', content: 'yes' },
    { name: 'apple-mobile-web-app-title', content: 'SOS Field' },
  ],
})
</script>

<template>
  <div class="field-app field-shell">
    <Head
      ><Meta
        name="viewport"
        content="width=device-width, initial-scale=1, viewport-fit=cover"
    /></Head>
    <header class="field-topbar">
      <div class="field-topbar-inner" :class="{ 'has-back': canGoBack }">
        <Button
          v-if="canGoBack"
          :aria-label="ft('Back')"
          :title="ft('Back')"
          icon="pi pi-arrow-left"
          rounded
          severity="contrast"
          text
          class="field-icon-button field-back-button"
          @click="goBack"
        />
        <NuxtLink to="/field" class="field-brand">
          <svg
            viewBox="0 0 44 44"
            width="42"
            height="42"
            fill="none"
            aria-hidden="true"
          >
            <rect x="1" y="1" width="42" height="42" rx="14" fill="#50427d" />
            <g
              transform="rotate(-7 22 23)"
              stroke="#302640"
              stroke-width="1.7"
              stroke-linejoin="round"
            >
              <path
                d="M10 17q0-3 3-3h5l2-3h8l2 4h2q3 0 3 4v11q0 3-3 3H13q-3 0-3-4z"
                fill="#ffd7ab"
              />
              <circle cx="23" cy="23" r="6" fill="#fff7e7" />
              <circle cx="23" cy="23" r="2.6" fill="#50427d" />
              <path d="M13 19h4" stroke-linecap="round" />
            </g>
          </svg>
          <span>SOS Field</span>
        </NuxtLink>
        <template v-if="isSignedIn">
          <button
            v-if="
              isPhotoPage && session?.department && allowedDepartments.length
            "
            type="button"
            class="field-department-chip"
            :class="`is-${session.department}`"
            :disabled="allowedDepartments.length < 2"
            :aria-label="
              ft('Department: {name}. Tap to switch.', {
                name: departmentLabel(session.department),
              })
            "
            @click="showDepartments = true"
          >
            {{ departmentLabel(session.department) }}
            <i
              v-if="allowedDepartments.length > 1"
              class="pi pi-angle-down"
              aria-hidden="true"
            />
          </button>
          <button
            type="button"
            class="field-language"
            :aria-label="
              otherLocale === 'es' ? 'Cambiar a español' : 'Switch to English'
            "
            @click="switchLanguage"
          >
            <i class="pi pi-globe" aria-hidden="true" />
            {{ otherLocale === 'es' ? 'Español' : 'English' }}
          </button>
          <span class="field-worker">{{
            session?.worker_name.trim().split(/\s+/)[0]
          }}</span>
          <Button
            :aria-label="ft('Sign out')"
            :title="ft('Sign out')"
            icon="pi pi-sign-out"
            rounded
            severity="contrast"
            text
            class="field-icon-button"
            @click="onSignOut"
          />
        </template>
        <button
          v-else
          type="button"
          class="field-language"
          :aria-label="
            otherLocale === 'es' ? 'Cambiar a español' : 'Switch to English'
          "
          @click="switchLanguage"
        >
          <i class="pi pi-globe" aria-hidden="true" />
          {{ otherLocale === 'es' ? 'Español' : 'English' }}
        </button>
      </div>
    </header>

    <nav
      v-if="isSignedIn"
      class="field-worker-nav"
      :aria-label="ft('Employee app')"
    >
      <NuxtLink to="/field" :aria-current="!canGoBack ? 'page' : undefined"
        ><i class="pi pi-home" aria-hidden="true" />{{ ft('Home') }}</NuxtLink
      >
      <NuxtLink
        v-if="allowedDepartments.length"
        to="/field/photos"
        :aria-current="isPhotoPage ? 'page' : undefined"
        ><i class="pi pi-camera" aria-hidden="true" />CompanyCam</NuxtLink
      >
      <NuxtLink
        to="/field/timesheet"
        :aria-current="route.path === '/field/timesheet' ? 'page' : undefined"
        ><i class="pi pi-clock" aria-hidden="true" />{{
          ft('Clocks')
        }}</NuxtLink
      >
      <NuxtLink
        v-if="session?.sales_enabled"
        to="/field/marketing"
        :aria-current="route.path.startsWith('/field/marketing') || route.path.startsWith('/field/sales') ? 'page' : undefined"
        ><i class="pi pi-dollar" aria-hidden="true" />{{
          ft('Sales')
        }}</NuxtLink
      >
    </nav>

    <main class="field-main">
      <section
        v-if="needsDepartment"
        class="field-gate field-panel"
        aria-labelledby="field-department-title"
      >
        <i class="pi pi-th-large field-gate-icon" aria-hidden="true" />
        <h1 id="field-department-title">{{ ft('Which department today?') }}</h1>
        <p>
          {{
            ft(
              'Your photos go to this department’s folder. You can switch later.'
            )
          }}
        </p>
        <FieldDepartmentPicker
          :allowed="allowedDepartments"
          :current="session?.department"
          :loading="switching"
          @choose="chooseDepartment"
        />
      </section>
      <section
        v-else-if="locked"
        class="field-gate field-panel"
        role="alert"
        aria-live="polite"
      >
        <template
          v-if="
            location.status.value === 'idle' ||
            location.status.value === 'checking'
          "
        >
          <i class="pi pi-spin pi-spinner field-gate-icon" aria-hidden="true" />
          <h1>{{ ft('Turning on location…') }}</h1>
          <p>
            {{ ft('When your phone asks, tap') }}
            <strong>{{ ft('Allow') }}</strong
            >.
            {{
              ft(
                'CompanyCam needs your location to stamp every photo and find your job.'
              )
            }}
          </p>
        </template>

        <template v-else-if="location.status.value === 'denied'">
          <i class="pi pi-map-marker field-gate-icon" aria-hidden="true" />
          <h1>{{ ft('Location is off') }}</h1>
          <p>
            {{
              ft(
                'You can’t use CompanyCam without location. Turn it on, then tap'
              )
            }}
            <strong>{{ ft('Try again') }}</strong
            >.
          </p>
          <ol v-if="isIos" class="field-gate-steps">
            <li>
              {{ ft('Open') }} <strong>{{ ft('Settings') }}</strong> →
              <strong>{{ ft('Privacy & Security') }}</strong> →
              <strong>{{ ft('Location Services') }}</strong
              >, {{ ft('and make sure it’s on.') }}
            </li>
            <li>
              {{ ft('In the same screen, open') }}
              <strong>{{ ft('Safari Websites') }}</strong>
              {{ ft('(or your browser) and choose') }}
              <strong>{{ ft('While Using the App') }}</strong
              >.
            </li>
            <li>
              {{ ft('Back in Safari, tap') }} <strong>aA</strong>
              {{ ft('in the address bar') }} →
              <strong>{{ ft('Website Settings') }}</strong> →
              <strong>{{ ft('Location') }}</strong> →
              <strong>{{ ft('Allow') }}</strong
              >.
            </li>
          </ol>
          <ol v-else class="field-gate-steps">
            <li>
              {{ ft('Tap the icon left of the address bar') }} →
              <strong>{{ ft('Permissions') }}</strong> →
              <strong>{{ ft('Location') }}</strong> →
              <strong>{{ ft('Allow') }}</strong
              >.
            </li>
            <li>
              {{
                ft(
                  'Make sure the phone’s location is on (swipe down from the top of the screen).'
                )
              }}
            </li>
          </ol>
        </template>

        <template v-else-if="location.status.value === 'unavailable'">
          <i class="pi pi-compass field-gate-icon" aria-hidden="true" />
          <h1>{{ ft('Looking for GPS…') }}</h1>
          <p>
            {{
              ft(
                'We can’t find your location yet. Step outside or near a window and wait a few seconds.'
              )
            }}
          </p>
        </template>

        <template v-else>
          <i
            class="pi pi-exclamation-circle field-gate-icon"
            aria-hidden="true"
          />
          <h1>{{ ft('This browser can’t share location') }}</h1>
          <p>
            {{ ft('Open CompanyCam in Safari (iPhone) or Chrome (Android).') }}
          </p>
        </template>

        <Button
          v-if="location.status.value !== 'checking'"
          :label="ft('Try again')"
          icon="pi pi-refresh"
          size="large"
          class="field-primary"
          fluid
          @click="retryLocation"
        />
      </section>
      <NuxtPage v-else />
    </main>

    <div
      v-if="needsPhotoLocation && (pending.length || failed.length || !online)"
      class="field-status"
      :class="
        !online ? 'is-offline' : pending.length ? 'is-uploading' : 'is-failed'
      "
      role="status"
    >
      <div class="field-status-inner">
        <FieldArtwork v-if="!online" scene="offline" />
        <i
          v-else
          class="pi"
          :class="
            pending.length
              ? uploading
                ? 'pi-spin pi-spinner'
                : 'pi-clock'
              : 'pi-exclamation-circle'
          "
          aria-hidden="true"
        />
        <span v-if="!online">
          <strong>{{ ft('No signal.') }}</strong>
          {{
            pending.length + failed.length === 1
              ? ft('1 photo saved on this phone.')
              : ft('{count} photos saved on this phone.', {
                  count: pending.length + failed.length,
                })
          }}
          <template v-if="pending.length">{{
            ft('Uploads resume when you reconnect.')
          }}</template>
        </span>
        <span v-else-if="pending.length">
          <strong>{{
            pending.length === 1
              ? ft('Uploading 1 photo…')
              : ft('Uploading {count} photos…', { count: pending.length })
          }}</strong>
          {{ ft('Keep this page open.') }}
        </span>
        <span v-else-if="failed.length">
          <strong>{{
            failed.length === 1
              ? ft('1 photo could not upload.')
              : ft('{count} photos could not upload.', {
                  count: failed.length,
                })
          }}</strong>
          {{ ft('Open the job to retry.') }}
        </span>
      </div>
    </div>

    <Dialog
      v-model:visible="showDepartments"
      :header="ft('Switch department')"
      modal
      position="bottom"
      append-to="self"
      :draggable="false"
      class="field-sheet"
    >
      <FieldDepartmentPicker
        :allowed="allowedDepartments"
        :current="session?.department"
        :loading="switching"
        @choose="chooseDepartment"
      />
    </Dialog>
  </div>

  <Toast position="top-center" class="field-app field-toast" />
</template>

<style lang="scss" src="~/assets/field.scss"></style>
